(function (global) {
    const HOWL_STATE = {
        PLAYING: 'howl-playing',
        PAUSED:  'howl-paused',
        PLAYED:  'howl-played'
    };

    class SoundItem {
        constructor(data) {
            this.id = data.id;
            this.classId = data.classId;
            this.ref = data.ref;
            this.text = data.text;
            this.url = data.url;
            this.linkedElement = data.linkedElement;
            this.sound = null;
            this.state = {
                isPlaying: false,
                isPlayed:  false,
                isPaused:  false
            };
        }

        getStateClass() {
            if (this.state.isPlaying) {
                return HOWL_STATE.PLAYING;
            }
            if (this.state.isPaused) {
                return HOWL_STATE.PAUSED;
            }
            if (this.state.isPlayed) {
                return HOWL_STATE.PLAYED;
            }
            return '';
        }
    }

    class AudioManager {
        constructor() {
            this.sound = null;
            this.playlists = new Map();
            this.playlistMaxSize = 5;
            this.playlistId = '';
            this.currentElementId = '';
            this.playingFromPlaylist = false;
            this.parentClasses = ['card', 'question-holder', 'sounds-holder', 'popover', 'accordion-item'];
            this.debug = false;
            this.logMissingSounds = true;
            this._cache = {
                loggedMissingSounds: []
            }
        }

        log() {
            if (this.debug) {
                console.debug('AudioManager:', ...arguments);
            }
        }

        showAllItems(parentElement) {
            parentElement.querySelectorAll('.howl-button').forEach(el => el.classList.add('d-inline-block'));
        }

        playSound(soundItem) {
            this.stopSound();

            if (!soundItem) {
                this.log(`playSound missing soundItem!`, soundItem);
                return;
            }

            this.log(`playSound`, soundItem);

            this.sound = new Howl({
                src:         [soundItem.url],
                autoplay:    true,
                html5:       true,
                loop:        false,
                onloaderror: (id, error) => {
                    this.sound = null;
                    console.warn(`Failed to load soundId: ${soundItem.id}, url: ${soundItem.url}`, {id, error});
                    this.reportMissingSound(soundItem, error);
                    this.playNext();
                }
            });

            this.sound.on('play', () => this.updateState(soundItem, 'play'));
            this.sound.on('stop', () => this.updateState(soundItem, 'stop'));
            this.sound.on('pause', () => this.updateState(soundItem, 'pause'));
            this.sound.on('end', () => {
                this.updateState(soundItem, 'end');
                this.playNext();
            });

            this.currentElementId = soundItem.id;
        }

        playByUrl(url, loop = false) {
            this.stopSound();
            if (url.indexOf('/recordings') === 0) {
                url = window.LGD.cdnUrl + url;
            }

            this.log('playByUrl', url, loop);

            this.sound = new Howl({
                src:         [url],
                autoplay:    true,
                html5:       true,
                loop,
                onloaderror: (_, error) => {
                    this.sound = null;
                    console.warn(`Failed to load sound: ${url}`, {id, error});
                },
                onend:       () => {
                    if (!loop) {
                        this.sound = null;
                    }
                }
            });
        }

        playNext() {
            if (this.playlistId && this.playingFromPlaylist) {

                const playlist = this.playlists.get(this.playlistId) || [];
                const currentIndex = playlist.findIndex(item => item.id === this.currentElementId);
                if (currentIndex < playlist.length - 1) {
                    this.playSound(playlist[currentIndex + 1]);
                } else {
                    this.log('Reached end of playlist');
                }
            }
        }

        removePlaylist(playlistId) {
            if (this.playlistId === playlistId) {
                this.playlistId = null;
            }

            if (this.playlists.has(playlistId)) {
                this.playlists.delete(playlistId);
                this.log('removePlaylist', playlistId)
            }
        }

        removeOldPlaylist() {
            for (const [key] of this.playlists) {
                if (key !== this.playlistId) {
                    this.log('removeOldPlaylist', key)
                    this.removePlaylist(key);
                    break;
                }
            }
        }

        setPlaylist(playlistId, sounds) {
            if (this.playlists.size >= this.playlistMaxSize) {
                this.removeOldPlaylist();
            }

            this.playlists.set(playlistId, sounds);
            this.log('setPlaylist', {
                id:    playlistId,
                items: sounds.length
            });
        }

        setPlaylistId(playlistId) {
            this.playlistId = playlistId;
        }

        stopSound() {
            if (typeof Howler !== "undefined" && typeof Howler == 'object') {
                Howler.stop();
            }

            if (this.sound) {
                this.sound.unload();
            }

            this.sound = null;
        }

        async createSoundItemsFromElements(playlistId, outerElement, fallbackRef) {
            const elements = outerElement.querySelectorAll('.howl-button');
            const soundIds = new Set();
            const soundButtons = [];
            let linkedElement = null;

            for (const [index, el] of elements.entries()) {
                const textElement = el.querySelector('.sm2-sound-text');
                const text = textElement ? textElement.textContent.trim() : null;
                await this.setRequiredDataAttributes(el, text, fallbackRef);

                if (index > 0 && el.dataset.playlist === "true" && el.dataset.playlistId !== playlistId) {
                    // Got new playlist
                    break;
                }

                if (el.dataset.linked) {
                    linkedElement = el;
                } else if (!soundIds.has(el.dataset.soundId)) {
                    soundButtons.push(el);
                    soundIds.add(el.dataset.soundId);
                }
            }

            if (linkedElement) {
                // Set the first soundId as soundId on the linked element
                linkedElement.dataset.soundId = soundIds.values().next().value;
            }

            return Promise.all(soundButtons.map((el, idx) => {
                const textElement = el.querySelector('.sm2-sound-text');
                return new SoundItem({
                    id:            el.dataset.soundId,
                    text:          textElement ? textElement.textContent.trim() : null,
                    url:           el.dataset.url,
                    classId:       el.dataset.classId || null,
                    ref:           el.dataset.ref || null,
                    linkedElement: idx === 0 && linkedElement ? linkedElement : undefined
                });
            }));
        }

        async setRequiredDataAttributes(element, text, fallbackRef) {
            if (!element.dataset.soundId) {
                element.dataset.soundId = this.generateUniqueId();
            }

            if (!element.dataset.ref && fallbackRef) {
                element.dataset.ref = fallbackRef;
            }

            if (!element.dataset.url && text) {
                element.dataset.url = await this.createSoundUrl(text, element.dataset.reader);
            }
        }

        generateUniqueId() {
            const timestamp = Date.now().toString(36);
            const randomPart = Math.random().toString(36).slice(2, 11);
            return `id-${timestamp}-${randomPart}`;
        }

        async createSoundUrl(text, reader) {
            if (!text || !reader) {
                return '';
            }

            if (!window.crypto || !window.crypto.subtle) {
                throw new Error("Browser does not support crypto");
            }

            const data = new TextEncoder().encode(text);
            const hashBuffer = await crypto.subtle.digest("SHA-256", data);
            const hash = Array.from(new Uint8Array(hashBuffer))
                .map(byte => byte.toString(16).padStart(2, '0'))
                .join('');

            return `${window.LGD.cdnUrl}/recordings/${reader}/${hash}.mp3`
        }

        async init() {
            const buttons = document.querySelectorAll('.howl-button.first');

            this.log(`init Found ${buttons.length} items`, buttons);

            document.body.addEventListener('click', (event) => {
                if (event.target instanceof HTMLElement && event.target.matches('.howl-button')) {
                    event.preventDefault();
                    this.handleClick(event.target);
                }
            });
        }

        async handlePlaylistClick(button) {
            if (!button.dataset.playlistId) {
                button.dataset.playlistId = `playlist-${this.generateUniqueId()}`;
            }

            this.setPlaylistId(button.dataset.playlistId)

            if (!this.playlists.has(this.playlistId)) {
                const fallbackRef = button.dataset.ref;
                const parentElement = this.getClosestSoundContainer(button);
                const playlistItems = await this.createSoundItemsFromElements(this.playlistId, parentElement, fallbackRef)

                this.showAllItems(parentElement);
                this.setPlaylist(this.playlistId, playlistItems);
            }

            this.playingFromPlaylist = true;

            // play first sound in playlist
            this.playSound(this.playlists.get(this.playlistId)[0]);
        }

        async handleClick(button) {

            if (button.classList.contains(HOWL_STATE.PLAYING)) {
                if (this.sound) {
                    this.sound.pause();
                }
                return;
            }

            if (button.classList.contains(HOWL_STATE.PAUSED)) {
                if (this.sound) {
                    this.sound.play();
                    return;
                }
            }

            if (button.dataset.playlist === 'true') {
                return this.handlePlaylistClick(button);
            }

            const textElement = button.querySelector('.sm2-sound-text')
            const text = textElement ? textElement.textContent : null;
            await this.setRequiredDataAttributes(button, text);

            this.playingFromPlaylist = false;
            this.playSound(new SoundItem({
                id:      button.dataset.soundId,
                text:    text,
                url:     button.dataset.url,
                classId: button.dataset.classId || null,
                ref:     button.dataset.ref || null,
            }))
        }

        getClosestSoundContainer(buttonElement) {
            let currentElement = buttonElement;

            // Loop over parents until we find an element matching one of the defined parentClasses, stopping after 5 iterations.
            for (let i = 0; i < 5; i++) {
                if (!currentElement) {
                    break;
                }

                if (this.parentClasses.some(className => currentElement.classList.contains(className))) {
                    return currentElement;
                }

                currentElement = currentElement.parentElement;
            }

            return currentElement;
        }

        updateState(soundItem, state) {
            soundItem.state.isPlaying = state === 'play';
            soundItem.state.isPaused = state === 'pause';
            soundItem.state.isPlayed = state === 'end' || state === 'stop';
            this.toggleSoundElementClasses(soundItem);
        }

        toggleSoundElementClasses(soundItem) {
            const updateClasses = (soundId) => {
                if (!soundId) {
                    return;
                }

                document.querySelectorAll('.howl-button[data-sound-id="' + soundId + '"]').forEach(el => {
                    el.classList.remove(HOWL_STATE.PLAYING, HOWL_STATE.PAUSED, HOWL_STATE.PLAYED);
                    el.classList.add(soundItem.getStateClass());
                })
            };

            updateClasses(soundItem.id);
        }


        reportMissingSound(sound, error) {
            if (!this.logMissingSounds) {
                return;
            }

            const payload = {
                text:    sound.text,
                url:     sound.url,
                ref:     sound.ref,
                classId: sound.classId,
                error:   error
            }

            if (!payload.ref) {
                payload.ref = window.location.pathname;

                if (window.location.pathname.indexOf('teorikurs') > -1) {
                    payload.ref += window.location.search;
                }
            }

            if (!payload.text) {
                return;
            }

            if (payload.url) {
                if (this._cache.loggedMissingSounds.indexOf(payload.url) > -1) {
                    return;
                }

                this._cache.loggedMissingSounds.push(payload.url);
            }

            this.log('reportMissingSound', sound, error);

            fetch('/raw/missing_sound', {
                method:  'POST',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'Content-Type':     'application/json',
                },
                body:    JSON.stringify(payload)
            })
                .then(response => {
                    if (!response.ok) {
                        throw new Error('error ' + response.statusText);
                    }
                    return response.json();
                })
                .catch(error => console.debug('failed to report missing sound, error:', error));
        }
    }

    global.AudioManager = new AudioManager();
    global.SoundItem = SoundItem;
    global.HOWL_STATE = HOWL_STATE;

    document.addEventListener('DOMContentLoaded', () => {
        global.AudioManager.init();
    })
})(window);